<script>
  import * as env from '$app/env/public';
  import t from '#lib/i18n.svelte.js';
  import FormLabel from '#lib/FormLabel.svelte';
  import SegmentedControl from '#lib/SegmentedControl.svelte';
  import DateInput from '#lib/DateInput.svelte';
  import IntegerInput from '#lib/IntegerInput.svelte';
  import EventChip from '#lib/EventChip.svelte';
  import Dialog from '#lib/Dialog.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import '#lib/system-styles/event-ui.css';
  import { format_date, create_utc_timestamp_that_appears_as_local_midnight } from '#lib/util.js';
  import { validate_series_pattern, generate_series_dates, MAX_OCCURRENCES } from '#lib/date_recurrence.js';
  import { getContext } from 'svelte';

  let {
    entry,
    is_editing,
  } = $props();

  let site_settings = getContext('site_settings');
  const timezone = env.PUBLIC_TIME_ZONE || 'Europe/Berlin';

  let is_existing_event = $derived(Boolean(entry.entry_id));

  let frequency = $state('none');
  let interval = $state(1);
  let end_mode = $state('end_date'); // 'end_date' | 'repeat_count'
  let end_date_utc = $state(undefined);
  let repeat_count = $state(5);

  // The number input can transiently hold empty/invalid values while typing
  let effective_interval = $derived(Number.isInteger(interval) && interval >= 1 ? interval : 1);

  let show_dates_preview_dialog = $state(false);
  let end_date_was_set = $state(false);

  // Preview dates for the series dates dialog
  let preview_dates = $derived.by(() => {
    if (frequency === 'none' || !entry.starts_at || !end_date_utc) return [];
    const end_date_local = utc_to_local_date_string(end_date_utc);
    try {
      return generate_series_dates(
        { starts_at: entry.starts_at, ends_at: entry.ends_at || entry.starts_at },
        { frequency, interval: effective_interval, end_date: end_date_local },
        timezone
      );
    } catch {
      return [];
    }
  });

  // Validation
  let validation = $derived.by(() => {
    if (frequency === 'none' || !entry.starts_at) return null;
    if (!end_date_utc) return { valid: false, error_key: 'series_invalid_end_date', occurrence_count: 0 };
    const end_date_local = utc_to_local_date_string(end_date_utc);
    return validate_series_pattern(
      { frequency, interval: effective_interval, end_date: end_date_local },
      entry.starts_at,
      timezone
    );
  });

  // Sync frequency selection to entry.series_pattern
  $effect(() => {
    if (frequency !== 'none' && end_date_utc) {
      const end_date_local = utc_to_local_date_string(end_date_utc);
      entry.series_pattern = {
        frequency,
        interval: effective_interval,
        end_date: end_date_local,
      };
    } else if (frequency === 'none' || !end_date_utc) {
      entry.series_pattern = undefined;
    }
  });

  // Set default end date when frequency is first selected in end_date mode
  $effect(() => {
    if (end_mode === 'end_date' && frequency !== 'none' && !end_date_utc && entry.starts_at && !end_date_was_set) {
      const start = new Date(entry.starts_at);
      const default_end = new Date(start);
      default_end.setMonth(default_end.getMonth() + 3);
      const date_str = `${default_end.getFullYear()}-${String(default_end.getMonth() + 1).padStart(2, '0')}-${String(default_end.getDate()).padStart(2, '0')}`;
      end_date_utc = create_utc_timestamp_that_appears_as_local_midnight(date_str, timezone).toISOString();
      end_date_was_set = true;
    }
    if (frequency === 'none') {
      end_date_was_set = false;
    }
  });

  /**
   * Auto-correct the end date to produce exactly MAX_OCCURRENCES events.
   * Uses binary search to find the latest valid end date.
   */
  function correct_end_date_to_max() {
    if (!entry.starts_at || frequency === 'none') return;
    const start = new Date(entry.starts_at);
    // Estimate: for daily that's MAX_OCCURRENCES days, weekly MAX*7, monthly MAX*30
    const multiplier = (frequency === 'daily' ? 1 : frequency === 'weekly' ? 7 : 30) * effective_interval;
    let candidate = new Date(start);
    candidate.setDate(candidate.getDate() + (MAX_OCCURRENCES - 1) * multiplier);
    // Binary search for exact fit
    let low = new Date(start);
    low.setDate(low.getDate() + 1);
    let high = candidate;
    for (let i = 0; i < 30; i++) {
      const mid = new Date((low.getTime() + high.getTime()) / 2);
      const mid_str = `${mid.getFullYear()}-${String(mid.getMonth() + 1).padStart(2, '0')}-${String(mid.getDate()).padStart(2, '0')}`;
      const result = validate_series_pattern(
        { frequency, interval: effective_interval, end_date: mid_str },
        entry.starts_at,
        timezone
      );
      if (result.valid && result.occurrence_count <= MAX_OCCURRENCES) {
        low = mid;
      } else {
        high = mid;
      }
    }
    const final_str = `${low.getFullYear()}-${String(low.getMonth() + 1).padStart(2, '0')}-${String(low.getDate()).padStart(2, '0')}`;
    end_date_utc = create_utc_timestamp_that_appears_as_local_midnight(final_str, timezone).toISOString();
  }

  /**
   * Compute end_date_utc from a desired repeat count (total occurrences including base).
   * Advances the start date by (count - 1) intervals to find the last occurrence date.
   */
  function compute_end_date_from_count(count) {
    if (!entry.starts_at || frequency === 'none' || !count || count < 2) return;
    const local_start = new Date(entry.starts_at);
    const target = count - 1; // additional occurrences beyond the base
    let end = new Date(local_start);
    switch (frequency) {
      case 'daily':
        end.setDate(end.getDate() + target * effective_interval);
        break;
      case 'weekly':
        end.setDate(end.getDate() + target * 7 * effective_interval);
        break;
      case 'monthly':
        end.setMonth(end.getMonth() + target * effective_interval);
        break;
    }
    const date_str = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    end_date_utc = create_utc_timestamp_that_appears_as_local_midnight(date_str, timezone).toISOString();
  }

  // When in repeat_count mode, recompute end date whenever count or frequency changes
  $effect(() => {
    if (end_mode === 'repeat_count' && frequency !== 'none' && entry.starts_at) {
      compute_end_date_from_count(repeat_count);
    }
  });

  function utc_to_local_date_string(utc_iso) {
    if (!utc_iso) return '';
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(new Date(utc_iso));
  }
</script>

{#if is_editing && !entry.series_id}
  <!--=========================================================================-->
  <!--======================== Recurrence Setup ================================-->
  <!--=========================================================================-->
  <div class='event-row'>
    <span class='text-control event-label'>{t('recurrence')}</span>
    <div class='event-value'>
      <SegmentedControl
        bind:value={frequency}
        options={[
          { value: 'none', title: t('does_not_repeat') },
          { value: 'daily', title: t('repeats_daily') },
          { value: 'weekly', title: t('repeats_weekly') },
          { value: 'monthly', title: t('repeats_monthly') },
        ]}
      />
    </div>
  </div>

  {#if frequency !== 'none'}
    <div class='event-row'>
      <FormLabel for='series_interval'>{t('series_interval')}</FormLabel>
      <div class='event-value'>
        <span class='text-control text-2'>{t('series_interval_every')}</span>
        <IntegerInput
          id='series_interval'
          class='event-field w-4 fit-content'
          variant='brand flush'
          show_spinners={false}
          min={1}
          max={52}
          bind:value={interval}
        />
        <span class='text-control text-2'>
          {t(`series_interval_unit_${frequency}_${effective_interval === 1 ? 'singular' : 'plural'}`)}
        </span>
      </div>
    </div>

    <div class='event-row'>
      <span class='text-control event-label'>{t('series_ends')}</span>
      <div class='event-value'>
        <SegmentedControl
          bind:value={end_mode}
          options={[
            { value: 'end_date', title: t('series_end_by_date') },
            { value: 'repeat_count', title: t('series_end_after_count') },
          ]}
        />
        {#if end_mode === 'end_date'}
          <DateInput
            {is_editing}
            bind:value={end_date_utc}
            id='series_end_date'
            aria-label={t('series_end_date')}
            class='event-field'
            variant='brand ghostly'
          />
        {:else}
          <IntegerInput
            id='series_repeat_count'
            aria_label={t('series_repeat_count')}
            class='event-field w-4 fit-content'
            variant='brand flush'
            show_spinners={false}
            min={2}
            max={MAX_OCCURRENCES}
            bind:value={repeat_count}
          />
          <span class='text-control text-2'>{t('series_occurrences_unit')}</span>
        {/if}
      </div>
    </div>

    {#if validation}
      <div class='event-row'>
        <span aria-hidden='true'></span>
        <div class='event-value'>
          {#if validation.valid}
            <button type='button' class='event-link text-control text-2' onclick={() => { show_dates_preview_dialog = true; }}>
              {t('series_will_create_n_events', { count: validation.occurrence_count })}
            </button>
          {:else}
            <p class='text-control text-error'>
              {t(validation.error_key, { count: validation.occurrence_count, max: MAX_OCCURRENCES })}
            </p>
            {#if validation.error_key === 'series_too_many_occurrences'}
              <button type='button' class='event-link text-control text-2' onclick={correct_end_date_to_max}>
                {t('series_correct_end_date')}
              </button>
            {/if}
          {/if}
        </div>
      </div>
      {#if validation.valid && is_existing_event}
        <div class='event-row'>
          <span aria-hidden='true'></span>
          <p class='text-control text-2'>
            {t('series_promote_notice', { count: validation.occurrence_count - 1 })}
          </p>
        </div>
      {/if}
    {/if}

    {#if show_dates_preview_dialog && preview_dates.length > 0}
      <Dialog onclose={() => { show_dates_preview_dialog = false; }}>
        <div class='flex-column gap-y g-4'>
          <h2 class='text-heading3'>{t('series_dates_preview_title')}</h2>
          <p class='text-control text-2'>
            {t('series_will_create_n_events', { count: preview_dates.length + 1 })}
          </p>
          <ol class='series-dates-list'>
            <li class='series-date-item text-control'>
              <span class='series-date-weekday'>{format_date(entry.starts_at, 'EEEE')}</span>
              <span class='series-date-date'>{format_date(entry.starts_at, 'DMY')}</span>
              {#if !entry.all_day}
                <span class='text-2'>{format_date(entry.starts_at, 'Hm')}</span>
              {/if}
              <EventChip>{t('base_event')}</EventChip>
            </li>
            {#each preview_dates as d}
              <li class='series-date-item text-control'>
                <span class='series-date-weekday'>{format_date(d.starts_at, 'EEEE')}</span>
                <span class='series-date-date'>{format_date(d.starts_at, 'DMY')}</span>
                {#if !entry.all_day}
                  <span class='text-2'>{format_date(d.starts_at, 'Hm')}</span>
                {/if}
              </li>
            {/each}
          </ol>
          <div class='flex-row'>
            <button onclick={() => { show_dates_preview_dialog = false; }}>
              <ButtonLabel variant='brand outlined'>
                {t('close')}
              </ButtonLabel>
            </button>
          </div>
        </div>
      </Dialog>
    {/if}
  {/if}
{/if}

<style lang='postcss'>
  .series-dates-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 60vh;
    overflow-y: auto;
  }
  .series-date-item {
    display: flex;
    align-items: center;
    gap: var(--size-3);
    padding-block: var(--size-2);
    border-top: 1px solid var(--color-default-stroke);
    font-variant-numeric: tabular-nums;
  }
  .series-date-weekday,
  .series-date-date {
    min-width: 7rem;
  }
</style>
